import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const OrganisationSettings = () => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState({
    name: "", email: "", phone: "", address: "",
    postal_code: "", city: "", country: "", coc_number: "", vat_number: "",
  });

  useEffect(() => {
    const fetch = async () => {
      if (!profile?.organisation_id) return;
      const { data } = await supabase.from("organisations").select("*").eq("id", profile.organisation_id).maybeSingle();
      if (data) {
        setOrg({
          name: data.name || "", email: data.email || "", phone: data.phone || "",
          address: data.address || "", postal_code: data.postal_code || "",
          city: data.city || "", country: data.country || "",
          coc_number: data.coc_number || "", vat_number: data.vat_number || "",
        });
      }
      setLoading(false);
    };
    fetch();
  }, [profile?.organisation_id]);

  const handleSave = async () => {
    if (!profile?.organisation_id) return;
    setSaving(true);
    const { error } = await supabase.from("organisations").update({
      name: org.name, email: org.email || null, phone: org.phone || null,
      address: org.address || null, postal_code: org.postal_code || null,
      city: org.city || null, country: org.country || null,
      coc_number: org.coc_number || null, vat_number: org.vat_number || null,
    }).eq("id", profile.organisation_id);
    setSaving(false);
    if (error) { toast.error(error.message); } else { toast.success(t("org.saved")); }
  };

  if (loading) {
    return (<div className="flex items-center justify-center p-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>);
  }

  return (
    <>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("org.title")}</h1>
      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2"><Label>{t("org.company_name")}</Label><Input value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.email")}</Label><Input type="email" value={org.email} onChange={(e) => setOrg({ ...org, email: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.phone")}</Label><Input value={org.phone} onChange={(e) => setOrg({ ...org, phone: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.address")}</Label><Input value={org.address} onChange={(e) => setOrg({ ...org, address: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.postal_code")}</Label><Input value={org.postal_code} onChange={(e) => setOrg({ ...org, postal_code: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.city")}</Label><Input value={org.city} onChange={(e) => setOrg({ ...org, city: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.country")}</Label><Input value={org.country} onChange={(e) => setOrg({ ...org, country: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.coc_number")}</Label><Input value={org.coc_number} onChange={(e) => setOrg({ ...org, coc_number: e.target.value })} /></div>
          <div className="space-y-2"><Label>{t("org.vat_number")}</Label><Input value={org.vat_number} onChange={(e) => setOrg({ ...org, vat_number: e.target.value })} /></div>
        </div>
        <Button className="mt-4" onClick={handleSave} disabled={saving}>{saving ? t("org.saving") : t("org.save")}</Button>
      </section>
    </>
  );
};

export default OrganisationSettings;
